import cloneDeep from 'lodash/cloneDeep';

import {
  FLEXIBLEEXP,
  OPERATORDISALLOW,
  OPERATORSTARTAFTER,
  XSCHEDULER,
} from '../../../constants';
import { Operators } from '../../../lib/Configurations';
import {
  AnalysisMetric,
  AnalysisMetricType,
  Constraint,
  ConstraintType,
  Experiment,
  ExperimentAnalysis,
  ExperimentMetadata,
  Preference,
  SampleSizeRequirement,
  SchedulingDef,
  Slotting,
  Variable,
} from '../../../lib/PlannerDTOs';
import {
  ExperimentAnalysisViewModel,
  ExperimentEventBasedMetricViewModel,
  ExperimentPreDefinedMetricViewModel,
  ExperimentVariableViewModel,
  ExperimentViewModel,
  SampleSizeEstimate,
} from './ExperimentViewModel';
import {
  formatUnixTimeInSingaporeTimezone,
  parseUnixTimeFromSingaporeTimezone,
} from '@/utils/dateTime';

export const DEFAULT_SAMPLE_SIZE: SampleSizeRequirement = {
  excludedVariables: [],
  baselineConversionRate: 0.3,
  stddev: Math.sqrt(0.3 * (1 - 0.3)),
  minimumDetectableEffect: 0.2,
  isRatio: true,
  relative: false,
  powerLevel: 0.8,
  alphaLevel: 0.05,
  sampleClusterSize: 0,
};

const EMPTY_EXPERIMENT_VARIABLE: ExperimentVariableViewModel = {
  name: '',
  strategy: '',
  geohashType: 5,
  domain: '',
  control: {
    value: 0,
    string: '',
    script: '',
  },
  treatments: [
    {
      value: 0,
      string: '',
      script: '',
    },
  ],
};

const EMPTY_EXPERIMENT: ExperimentViewModel = {
  variables: [EMPTY_EXPERIMENT_VARIABLE],
  name: '',
  productManager: '',
  productAnalyst: '',
  start: undefined,
  end: undefined,
  targeting: 'dynamic',
  city: '',
  reg: '',
  percentage: 0,
  description: '',
  notifyToReschedule: false,
  // Changing the original constraint to empty
  additionalConstraints: [
    { target: '', op: '', value: '', type: ConstraintType.Standard },
  ],
  sampleSize: DEFAULT_SAMPLE_SIZE,
  sampleSizeEstimate: {
    estimate: {
      variables: [],
      suggestedPercentage: 0,
      populationSize: null,
    },
    override: false,
    reason: '',
    overrideMaxAvailablePopulation: false,
    overrideMaxAvailablePopulationReason: '',
  },
};

export const isConstraintValid = (c: Constraint): boolean =>
  !!(c && c.value && c.op && c.target);

const splitMetrics = (metrics: string): string[] =>
  metrics ? metrics.split(',').map((metric) => metric.trim()) : [];

export function transformToVariableDto(
  variableVm: ExperimentVariableViewModel,
): Variable {
  const {
    name,
    strategy,
    geohashType,
    assignBy,
    childAssignBy,
    control,
    treatments,
  } = variableVm;
  return {
    name,
    strategy,
    geohashType,
    choices: [control, ...treatments],
    facets: assignBy
      ? childAssignBy
        ? [assignBy, childAssignBy]
        : [assignBy]
      : [],
  };
}

export function transformToAnalyisDTO(
  analysisVm: ExperimentAnalysisViewModel,
): ExperimentAnalysis {
  const { preDefined, populationFilter, eventBased, schemaVersion } =
    analysisVm;
  const analysisMetrics: AnalysisMetric[] = [];
  for (const v of eventBased) {
    if (v !== undefined && v.name !== '') {
      const params: { [key: string]: string } = {};
      params.startEventName = v.start!.event!;
      params.startEventState = v.start!.state!;
      params.endEventName = v.end!.event!;
      params.endEventState = v.end!.state!;
      analysisMetrics.push({
        id: v.id,
        name: v.name!,
        isPrimaryMetric: v.isPrimaryMetric!,
        type: AnalysisMetricType.EventBased,
        valueType: 'Continuous',
        parameters: params,
      });
    }
  }

  for (const v of preDefined) {
    analysisMetrics.push({
      id: v.id,
      name: v.name!,
      isPrimaryMetric: v.isPrimaryMetric!,
      type: AnalysisMetricType.PreDefined,
      valueType: v.valueType!,
      description: v.description,
    });
  }
  const analysisConfig: ExperimentAnalysis = {
    analysisConfig: {
      metrics: analysisMetrics,
      populationFilterEvent: populationFilter.event!,
      populationFilterState: populationFilter.state!,
    },
    schemaVersion,
  };

  return analysisConfig;
}

export function transformFromAnalysisDTO(
  analysis: ExperimentAnalysis,
): ExperimentAnalysisViewModel {
  const { analysisConfig, schemaVersion } = analysis;
  const eventBasedMetrics: ExperimentEventBasedMetricViewModel[] = [];
  const preDefinedMetrics: ExperimentPreDefinedMetricViewModel[] = [];

  if (analysisConfig!.metrics) {
    for (const v of analysisConfig!.metrics) {
      if (v.type === AnalysisMetricType.PreDefined) {
        const metric = {
          name: v.name,
          isPrimary: v.isPrimaryMetric,
          id: v.id,
          valueType: v.valueType,
          description: v.description,
        };
        preDefinedMetrics.push(metric);
      }

      if (v.type === AnalysisMetricType.EventBased) {
        const metric = {
          name: v.name,
          start: {
            event: v.parameters!.startEventName,
            state: v.parameters!.startEventState,
          },
          end: {
            event: v.parameters!.endEventName,
            state: v.parameters!.endEventState,
          },
          isPrimary: v.isPrimaryMetric,
        };
        eventBasedMetrics.push(metric);
      }
    }
  }

  return {
    populationFilter: {
      event: analysisConfig!.populationFilterEvent,
      state: analysisConfig!.populationFilterState,
    },
    schemaVersion,
    eventBased: eventBasedMetrics,
    preDefined: preDefinedMetrics,
  };
}

export function transformToDto(experimentVm: ExperimentViewModel): Experiment {
  const {
    name,
    metrics,
    description,
    notifyToReschedule,
    start,
    end,
    targeting,
    city,
    ctry,
    reg,
    percentage,
    group,
    additionalConstraints,
    sampleSize,
    sampleSizeEstimate,
    productManager,
    productAnalyst,
    isFlexible,
    overlapByDefault,
    flexibleStartAfterDate,
    schedulingTags,
    targetBy,
  } = experimentVm;

  // Variables
  const variables: Variable[] = experimentVm.variables.map(
    transformToVariableDto,
  );

  // Metadata
  let metadata: ExperimentMetadata | undefined;
  if (description !== undefined) {
    metadata = { description };
  }

  if (notifyToReschedule !== undefined) {
    metadata = { ...metadata, notifyToReschedule };
  }

  // Variables Metadata
  metadata = { ...metadata, skipFacetCheck: {} };
  for (const v of experimentVm.variables) {
    if (v.skipFacetCheck) {
      metadata!.skipFacetCheck![v.name] = v.skipFacetCheckReason
        ? v.skipFacetCheckReason
        : '';
    }
  }

  metadata = { ...metadata, productAnalyst, productManager };

  // Sample Size Requirement
  // remove sample size data if it is unsupported assignment strategy
  if (!isTimeSliceOrGroupStrategy(experimentVm.variables)) {
    metadata = {
      ...metadata,
      sampleSize: { ...metadata.sampleSize, required: {} },
    };
    if (sampleSize !== undefined) {
      metadata!.sampleSize!.required = sampleSize;
    }
    if (sampleSizeEstimate !== undefined) {
      metadata!.sampleSize!.estimated = sampleSizeEstimate.estimate;
      metadata!.sampleSize!.override = sampleSizeEstimate.override;
      metadata!.sampleSize!.reason = sampleSizeEstimate.reason;
      metadata!.sampleSize!.overrideMaxAvailablePopulation =
        sampleSizeEstimate.overrideMaxAvailablePopulation;
      metadata!.sampleSize!.overrideMaxAvailablePopulationReason =
        sampleSizeEstimate.overrideMaxAvailablePopulationReason;
    }
  }

  // Slotting
  const slotting: Slotting = {};

  if (targeting === 'dynamic') {
    slotting.byPercentage = percentage || 0;
  }

  if (targeting === 'static') {
    slotting.byGroup = group;
  }

  // Constraints
  const constraints: Constraint[] = [];

  // start time
  if (start) {
    constraints.push({
      target: 'ts',
      op: '>',
      value: formatUnixTimeInSingaporeTimezone(start).toString(),
    });
  }

  // end time
  if (end) {
    constraints.push({
      target: 'ts',
      op: '<',
      value: formatUnixTimeInSingaporeTimezone(end).toString(),
    });
  }

  // city constraint
  if (targeting === 'dynamic' && city) {
    let op = '=';
    let value = city;
    if (Array.isArray(city)) {
      op = 'in';
      value = '[' + city.join(',') + ']';
    }
    constraints.push({
      target: 'city',
      op,
      value,
    });
  }
  // country constraint
  if (targeting === 'dynamic' && ctry) {
    let op = '=';
    let value = ctry;
    if (Array.isArray(ctry)) {
      op = 'in';
      value = '[' + ctry.join(',') + ']';
    }
    constraints.push({
      target: 'ctry',
      op,
      value,
    });
  }

  // region constraint
  if (targeting === 'dynamic' && reg) {
    constraints.push({
      target: 'reg',
      op: '=',
      value: reg,
    });
  }

  // Additional constraints
  if (additionalConstraints) {
    constraints.push(...additionalConstraints.filter(isConstraintValid));
  }

  let schedulingDefinition: SchedulingDef;
  schedulingDefinition = { is_flexible: false, tags: [], conditions: [] };
  // Scheduling Definition
  if (isFlexible) {
    const durationInDays: number =
      start && end ? Math.abs(end.getDate() - start.getDate()) + 1 : 0;
    const timeOptions = {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    } as const;
    const preferences: Preference[] =
      start && end
        ? [
            {
              key: 'start',
              value: start.toLocaleTimeString('en-GB', timeOptions),
            },
            { key: 'end', value: end.toLocaleTimeString('en-GB', timeOptions) },
          ]
        : [];
    if (flexibleStartAfterDate) {
      schedulingDefinition = Object.assign(schedulingDefinition, {
        conditions: [
          ...schedulingDefinition.conditions,
          {
            key: FLEXIBLEEXP,
            operator: OPERATORSTARTAFTER,
            value: flexibleStartAfterDate.toISOString(),
          },
        ],
      });
    }

    schedulingDefinition = Object.assign(schedulingDefinition, {
      is_flexible: true,
      duration_in_days: durationInDays,
      preferences,
    });
  }

  if (schedulingTags?.length) {
    schedulingDefinition = Object.assign(schedulingDefinition, {
      tags: [
        ...schedulingDefinition.tags,
        {
          key: XSCHEDULER,
          values: schedulingTags,
        },
      ],
    });
  }

  if (targeting === 'dynamic') {
    if (overlapByDefault) {
      schedulingDefinition = Object.assign(schedulingDefinition, {
        conditions: [
          ...schedulingDefinition.conditions,
          {
            key: XSCHEDULER,
            operator: OPERATORDISALLOW,
            value: '',
          },
        ],
      });
    }
  }

  const experiment: Experiment = {
    name,
    variables,
    targetBy,
    metrics: splitMetrics(metrics!),
    slotting,
    constraints,
    metadata,
    schedulingDefinition,
  };

  // TODO: Do we need this transformation? should remove otherwise
  return Object.keys(experiment).reduce((map, key) => {
    const val = experiment[key];
    if (val || val === 0 || val === '') {
      map[key] = val;
    }
    return map;
  }, {} as Experiment);
}

function transformFromVariableDto(
  variable: Variable,
): ExperimentVariableViewModel {
  const { name, strategy, geohashType, facets, choices, domain } = variable;
  const [control, ...treatments] = choices;
  return {
    name,
    domain,
    strategy,
    geohashType,
    control,
    treatments,
    assignBy: facets![0],
    childAssignBy: facets?.length > 1 ? facets[1] : '',
  };
}

export function transformFromDto(experiment: Experiment): ExperimentViewModel {
  if (!experiment) return EMPTY_EXPERIMENT;
  const { name, constraints, slotting, metadata, targetBy } = experiment || {};

  // Variables
  const variables: ExperimentVariableViewModel[] = experiment?.variables?.map(
    transformFromVariableDto,
  );

  // Metrics
  const metrics = experiment?.metrics?.join(', ');

  // Scheduling
  const isFlexible = experiment?.schedulingDefinition?.is_flexible;

  let description: string | undefined;
  let notifyToReschedule: boolean | undefined;
  let ssr: SampleSizeRequirement;
  let sse: SampleSizeEstimate;

  let targeting: 'dynamic' | 'static' | undefined;
  let percentage: number | undefined;
  let group: string | undefined;

  let city: string | undefined;
  let ctry: string | undefined;

  let flexibleStartAfterDate: Date;

  const schedulingTags: string[] = [];
  const allowedTags: string[] = [];
  const disallowedTags: string[] = [];

  // Slotting
  if (slotting?.byPercentage || slotting?.byPercentage === 0) {
    targeting = 'dynamic';
    percentage = slotting?.byPercentage;
    if (ctry != null) {
      ctry = '';
    } else {
      city = '';
    }
  }

  if (slotting?.byGroup) {
    targeting = 'static';
    group = slotting.byGroup;
  }

  // Metadata
  ssr = {};
  sse = {};
  if (metadata) {
    description = metadata.description!;
    notifyToReschedule = metadata.notifyToReschedule!;
    if (metadata.skipFacetCheck) {
      Object.keys(metadata.skipFacetCheck).forEach((key) => {
        for (const v of variables) {
          if (v.name === key) {
            v.skipFacetCheck = true;
            v.skipFacetCheckReason = metadata.skipFacetCheck![key];
          }
        }
      });
    }
    if (metadata.sampleSize && metadata.sampleSize.required) {
      ssr = { ...metadata.sampleSize.required };
    }
    if (metadata.sampleSize && metadata.sampleSize.estimated) {
      sse = {
        estimate: metadata.sampleSize.estimated,
        override: metadata.sampleSize.override,
        reason: metadata.sampleSize.reason,
        overrideMaxAvailablePopulation:
          metadata.sampleSize.overrideMaxAvailablePopulation,
        overrideMaxAvailablePopulationReason:
          metadata.sampleSize.overrideMaxAvailablePopulationReason,
      };
    }
  }

  // Constraints
  let start: Date | undefined;
  let end: Date | undefined;
  const additionalConstraints: any[] = [];

  if (constraints) {
    for (const contraint of constraints) {
      if (contraint.op === '>' && contraint.target === 'ts') {
        start = parseUnixTimeFromSingaporeTimezone(
          parseInt(contraint.value!, 10),
        );
        continue;
      }

      if (contraint.op === '<' && contraint.target === 'ts') {
        end = parseUnixTimeFromSingaporeTimezone(
          parseInt(contraint.value!, 10),
        );
        continue;
      }

      if (contraint.op === '=' && contraint.target === 'city') {
        city = contraint.value!;
        continue;
      }

      if (contraint.op === '=' && contraint.target === 'ctry') {
        ctry = contraint.value;
        continue;
      }

      additionalConstraints.push(contraint);
    }
  }

  // scheduling definition
  experiment?.schedulingDefinition?.conditions?.forEach((condition) => {
    switch (condition.key) {
      case FLEXIBLEEXP:
        if (condition.operator === 'start_after') {
          flexibleStartAfterDate = new Date(condition.value);
        }
        break;
      case XSCHEDULER:
        if (condition.operator === 'allow') {
          allowedTags.push(condition.value);
        } else if (condition.operator === 'disallow') {
          disallowedTags.push(condition.value);
        }
    }
  });

  // scheduling definition tags
  experiment?.schedulingDefinition?.tags?.forEach((tag) => {
    switch (tag.key) {
      case XSCHEDULER:
        schedulingTags.push(...tag.values);
    }
  });

  return {
    variables,
    name,
    metrics,
    start,
    end,
    targeting,
    city,
    ctry,
    percentage,
    group,
    description,
    notifyToReschedule,
    additionalConstraints,
    sampleSize: ssr,
    sampleSizeEstimate: sse,
    isFlexible,
    flexibleStartAfterDate,
    targetBy,
    allowedTags,
    disallowedTags,
    schedulingTags,
  };
}

const timeSliceStrategies = [
  'timeSlice',
  'timeSliceShuffle',
  'timeSliceGrouped',
  'geoTimeSliceShuffle',
];

const geohashStrategies = ['geoTimeSliceShuffle'];

const groupStrategies = ['explicitGroup', 'explicitRange'];

export function isTimeSliceOrGroupStrategy(
  variables: ExperimentVariableViewModel[],
): boolean {
  const timeSliceAndGroupStrategies =
    timeSliceStrategies.concat(groupStrategies);
  for (const v of variables) {
    if (timeSliceAndGroupStrategies.includes(v.strategy)) {
      return true;
    }
  }
  return false;
}

export function isTimeSliceStrategy(strategy: string): boolean {
  return timeSliceStrategies.includes(strategy);
}

export function isGeohashStrategy(strategy: string): boolean {
  return geohashStrategies.includes(strategy);
}

// Remove timestamp part of pax.abacusexperimentfull:15324061218"
// and return pax.abacusexperimentfull
export const truncateVersion = (nameWithVersion: string) =>
  nameWithVersion.replace(/:\d+$/, '');

export function transformCloneExperiment(initialExperiment: Experiment) {
  const cloneExperiment = cloneDeep(initialExperiment);

  // Rename
  const name = cloneExperiment.name;
  cloneExperiment.name = `clone_of_${name.substring(name.indexOf('.') + 1)}`;

  // Remove group versions
  if (cloneExperiment.slotting && cloneExperiment.slotting.byGroup) {
    cloneExperiment.slotting.byGroup = truncateVersion(
      cloneExperiment.slotting.byGroup,
    );
  }

  cloneExperiment.constraints = cloneExperiment.constraints.filter(
    (c) => c.type !== ConstraintType.Targeting,
  );

  cloneExperiment.constraints.forEach((c) => {
    if (
      c.type === ConstraintType.Standard &&
      (c.op === Operators.in || c.op === Operators['not in'])
    ) {
      c.value = truncateVersion(c.value);
    }
  });

  cloneExperiment.variables.forEach((variable) => {
    variable.choices.forEach((choice) => {
      if (choice.group) {
        choice.group = truncateVersion(choice.group);
      }

      if (choice.script) {
        choice.script = truncateVersion(choice.script);
      }

      // remove variant/choice id
      delete choice.id;
    });
  });

  return cloneExperiment;
}
